import { AI_PRIVACY_ENABLED } from '../config';
import type { ChatMessage } from '../core/llm';
import { replaceSensitive, type SensitiveKind } from './detectors';
import { SECRET_PLACEHOLDER } from './gateway';

// ----------------------------------------------------------------------
// Last line of defence, run on every request to the model. The gateway should
// already have replaced everything; whatever still matches here slipped past
// it (a missing policy, the lecture report, a legacy code path) and is removed
// for good. Only the counts are reported — never the values.
// ----------------------------------------------------------------------

export type RedactionCounts = Partial<Record<SensitiveKind, number>>;

const redactText = (text: string, counts: RedactionCounts): string =>
  replaceSensitive(text, (match) => {
    counts[match.kind] = (counts[match.kind] ?? 0) + 1;
    return match.kind === 'SECRET' ? SECRET_PLACEHOLDER : `[[REDACTED_${match.kind}]]`;
  });

const redactJsonValue = (value: unknown, counts: RedactionCounts): unknown => {
  if (typeof value === 'string') return redactText(value, counts);
  if (Array.isArray(value)) return value.map((item) => redactJsonValue(item, counts));
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, redactJsonValue(entry, counts)])
    );
  }
  return value;
};

/**
 * Tool messages and function-call arguments are JSON: only string values are
 * scanned, so a number such as an amount of 900000000 is never taken for a
 * phone, and the result stays valid JSON.
 */
const redactJsonString = (raw: string, counts: RedactionCounts): string => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return redactText(raw, counts);
  }
  return JSON.stringify(redactJsonValue(parsed, counts));
};

const redactContent = <T>(
  content: T,
  counts: RedactionCounts,
  redact: (text: string, counts: RedactionCounts) => string
): T => {
  if (typeof content === 'string') return redact(content, counts) as T;
  if (Array.isArray(content)) {
    return content.map((part: unknown) =>
      typeof part === 'object' && part !== null && 'text' in part && typeof part.text === 'string'
        ? { ...part, text: redact(part.text, counts) }
        : part
    ) as T;
  }
  return content;
};

export const redactResidual = (
  messages: ChatMessage[]
): { messages: ChatMessage[]; counts: RedactionCounts } => {
  const counts: RedactionCounts = {};
  if (!AI_PRIVACY_ENABLED) return { messages, counts };

  const redacted = messages.map((message) => {
    const redact = message.role === 'tool' ? redactJsonString : redactText;
    const next = {
      ...message,
      content: redactContent(message.content, counts, redact),
    } as ChatMessage;
    if (next.role === 'assistant' && next.tool_calls) {
      next.tool_calls = next.tool_calls.map((call) =>
        call.type === 'function'
          ? {
              ...call,
              function: {
                ...call.function,
                arguments: redactJsonString(call.function.arguments, counts),
              },
            }
          : call
      );
    }
    return next;
  });

  return { messages: redacted, counts };
};
